import { animate } from 'framer-motion';
import type { AnimationPlaybackControls } from 'framer-motion';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { WritingChunks } from '@/types/frontend-types';
import {
  CYCLE_END_EMPTY_LINE_COUNT,
  CbullshitLineTypingMode,
  TYPEWRITER_START_DELAY_MS,
  appendVisibleLine,
  createLineDropDisplayLine,
  getLineTypingPlan,
  getNextLineIndex,
  getWordTypingFrames,
  isCycleComplete,
  type ActiveLineTypingInput,
  type DisplayLine,
} from './Cbullshitcoding.typing';
import { CbullshitcodingTimingEventType } from './Cbullshitcoding.types';

export interface CbullshitcodingAnimationTimingEventInput {
  type: CbullshitcodingTimingEventType;
  timestampMs: number;
  stringIndex?: number;
  line?: string;
}

export type CbullshitcodingAnimationTimingEmitter = (
  input: CbullshitcodingAnimationTimingEventInput,
) => void;

export interface CbullshitcodingAnimationLoopInput {
  activeStrings: DisplayLine[];
  writingChunks: WritingChunks;
  loop: boolean;
  clearOnEnd: boolean;
  effectiveCharTypeSpeedMs: number;
  effectiveWordTypeSpeedMs: number;
  pauseBetweenLinesMs: number;
  trailingLineDropCount: number;
  trailingLineDropPauseMs: number;
  onCycleCompleteRef: MutableRefObject<(() => void) | undefined>;
  emitTimingEvent: CbullshitcodingAnimationTimingEmitter;
  cycleStartedAtRef: MutableRefObject<number | null>;
  lineStartedAtRef: MutableRefObject<number | null>;
  controlsRef: MutableRefObject<AnimationPlaybackControls | undefined>;
  hasContentRef: MutableRefObject<boolean>;
  resetTypingState: () => void;
  setCompletedLines: Dispatch<SetStateAction<DisplayLine[]>>;
  setCurrentLineIndex: Dispatch<SetStateAction<number>>;
  setCurrentTypingLine: Dispatch<SetStateAction<string>>;
  stringIndexRef: MutableRefObject<number>;
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
}

interface AnimationLoopLifecycle {
  isMounted: boolean;
}

interface CbullshitcodingAnimationLoopContext
  extends CbullshitcodingAnimationLoopInput {
  lifecycle: AnimationLoopLifecycle;
}

interface AnimationLoopContextInput {
  context: CbullshitcodingAnimationLoopContext;
}

interface EmptyLineInput extends AnimationLoopContextInput {
  count: number;
  index?: number;
}

interface ScheduleNextLineInput extends AnimationLoopContextInput {
  pauseMs: number;
}

interface CycleCompletionInput extends AnimationLoopContextInput {
  nextLineIndex: number;
}

interface AppendCompletedLineInput extends AnimationLoopContextInput {
  line: DisplayLine;
}

interface ActiveLineHandlerInput extends AnimationLoopContextInput {
  activeLine: ActiveLineTypingInput;
}

type ActiveLineHandler = (input: ActiveLineHandlerInput) => void;

const MS_PER_SECOND = 1000;

function getTimestampMs(): number {
  return performance.now();
}

function appendCompletedLine(input: AppendCompletedLineInput): void {
  input.context.setCompletedLines((previousLines) =>
    appendVisibleLine({ lines: previousLines, line: input.line }),
  );
}

function printEmptyLines(input: EmptyLineInput): void {
  const emptyLineIndex = input.index ?? 0;
  if (!input.context.lifecycle.isMounted || emptyLineIndex >= input.count) {
    return;
  }

  appendCompletedLine({
    context: input.context,
    line: createLineDropDisplayLine(),
  });

  input.context.timeoutRef.current = setTimeout(() => {
    if (input.context.lifecycle.isMounted) {
      printEmptyLines({
        context: input.context,
        count: input.count,
        index: emptyLineIndex + 1,
      });
    }
  }, input.context.pauseBetweenLinesMs);
}

function scheduleNextLine(input: ScheduleNextLineInput): void {
  input.context.timeoutRef.current = setTimeout(() => {
    if (input.context.lifecycle.isMounted) {
      typeNextLine({ context: input.context });
    }
  }, input.pauseMs);
}

function printCycleEndLinesIfNeeded(input: AnimationLoopContextInput): void {
  if (!input.context.clearOnEnd) {
    return;
  }

  printEmptyLines({
    context: input.context,
    count: CYCLE_END_EMPTY_LINE_COUNT,
  });
}

function finishCycleIfComplete(input: CycleCompletionInput): boolean {
  if (!isCycleComplete({ nextLineIndex: input.nextLineIndex })) {
    return false;
  }

  input.context.emitTimingEvent({
    type: CbullshitcodingTimingEventType.CycleComplete,
    timestampMs: getTimestampMs(),
  });
  input.context.onCycleCompleteRef.current?.();
  if (input.context.loop) {
    return false;
  }

  printCycleEndLinesIfNeeded({ context: input.context });
  return true;
}

function completeLine(input: ActiveLineHandlerInput): void {
  appendCompletedLine({
    context: input.context,
    line: input.activeLine.line,
  });
  input.context.setCurrentTypingLine('');
  input.context.emitTimingEvent({
    type: CbullshitcodingTimingEventType.LineComplete,
    timestampMs: getTimestampMs(),
    stringIndex: input.activeLine.lineIndex,
    line: input.activeLine.lineText,
  });

  const nextLineIndex = getNextLineIndex({
    lineIndex: input.activeLine.lineIndex,
    lineCount: input.context.activeStrings.length,
  });
  input.context.stringIndexRef.current = nextLineIndex;

  if (finishCycleIfComplete({ context: input.context, nextLineIndex })) {
    return;
  }

  scheduleNextLine({
    context: input.context,
    pauseMs: input.activeLine.pauseMs,
  });
}

function typeWords(input: ActiveLineHandlerInput): void {
  const typingFrames = getWordTypingFrames(input.activeLine.lineText);
  let wordIndex = 0;

  function typeNextWord(): void {
    if (
      !input.context.lifecycle.isMounted ||
      wordIndex >= typingFrames.length
    ) {
      completeLine(input);
      return;
    }

    input.context.setCurrentTypingLine(typingFrames[wordIndex]);
    wordIndex += 1;

    input.context.timeoutRef.current = setTimeout(() => {
      if (input.context.lifecycle.isMounted) {
        typeNextWord();
      }
    }, input.context.effectiveWordTypeSpeedMs);
  }

  typeNextWord();
}

function typeLetters(input: ActiveLineHandlerInput): void {
  const controls = animate(0, input.activeLine.lineText.length, {
    duration:
      input.activeLine.lineText.length *
      (input.context.effectiveCharTypeSpeedMs / MS_PER_SECOND),
    ease: 'linear',
    onUpdate: (latest) => {
      if (!input.context.lifecycle.isMounted) {
        return;
      }

      const charCount = Math.round(latest);
      input.context.setCurrentTypingLine(
        input.activeLine.lineText.slice(0, charCount),
      );
    },
    onComplete: () => {
      if (!input.context.lifecycle.isMounted) {
        return;
      }

      completeLine(input);
    },
  });

  input.context.controlsRef.current = controls;
}

const LINE_TYPING_HANDLERS: Record<CbullshitLineTypingMode, ActiveLineHandler> = {
  [CbullshitLineTypingMode.Instant]: completeLine,
  [CbullshitLineTypingMode.Word]: typeWords,
  [CbullshitLineTypingMode.Letter]: typeLetters,
};

function typeNextLine(input: AnimationLoopContextInput): void {
  if (!input.context.lifecycle.isMounted) {
    return;
  }

  const currentIndex = input.context.stringIndexRef.current;
  const currentLine = input.context.activeStrings[currentIndex];

  if (!currentLine) {
    return;
  }

  const lineText = currentLine.plain;
  input.context.setCurrentLineIndex(currentIndex);
  const lineTypingPlan = getLineTypingPlan({
    lineText,
    lineIndex: currentIndex,
    lineCount: input.context.activeStrings.length,
    writingChunks: input.context.writingChunks,
    trailingLineDropCount: input.context.trailingLineDropCount,
    trailingLineDropPauseMs: input.context.trailingLineDropPauseMs,
    pauseBetweenLinesMs: input.context.pauseBetweenLinesMs,
  });
  input.context.lineStartedAtRef.current = getTimestampMs();
  input.context.emitTimingEvent({
    type: CbullshitcodingTimingEventType.LineStart,
    timestampMs: input.context.lineStartedAtRef.current,
    stringIndex: currentIndex,
    line: lineText,
  });

  LINE_TYPING_HANDLERS[lineTypingPlan.mode]({
    context: input.context,
    activeLine: {
      line: currentLine,
      lineText,
      lineIndex: currentIndex,
      pauseMs: lineTypingPlan.pauseMs,
    },
  });
}

function startInitialLineTimeout(
  input: AnimationLoopContextInput,
): ReturnType<typeof setTimeout> {
  return setTimeout(() => {
    if (input.context.lifecycle.isMounted) {
      typeNextLine({ context: input.context });
    }
  }, TYPEWRITER_START_DELAY_MS);
}

function stopAnimationLoop(input: {
  context: CbullshitcodingAnimationLoopContext;
  startTimeout: ReturnType<typeof setTimeout>;
}): void {
  input.context.lifecycle.isMounted = false;
  clearTimeout(input.startTimeout);
  if (input.context.controlsRef.current) {
    input.context.controlsRef.current.stop();
  }
  if (input.context.timeoutRef.current) {
    clearTimeout(input.context.timeoutRef.current);
  }
}

export function startCbullshitcodingAnimationLoop(
  input: CbullshitcodingAnimationLoopInput,
): () => void {
  const context: CbullshitcodingAnimationLoopContext = {
    ...input,
    lifecycle: { isMounted: true },
  };

  context.resetTypingState();
  context.cycleStartedAtRef.current = getTimestampMs();
  context.emitTimingEvent({
    type: CbullshitcodingTimingEventType.CycleStart,
    timestampMs: context.cycleStartedAtRef.current,
  });
  context.hasContentRef.current = true;

  const startTimeout = startInitialLineTimeout({ context });

  return () => {
    stopAnimationLoop({ context, startTimeout });
  };
}
